import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface ArticleCardProps {
  id: number
  title: string
  description: string
  thumbnail: string
  category: string
  categoryName: string
  author: { name: string; avatar: string }
  views: number
  likes: number
  date: string
  featured?: boolean
}

export default function ArticleCard({
  id,
  title,
  description,
  thumbnail,
  category,
  categoryName,
  author,
  views,
  likes,
  date,
  featured,
}: ArticleCardProps) {
  const { t } = useTranslation('common')

  const formatCount = (n: number) => {
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万`
    return n.toLocaleString()
  }

  return (
    <Link
      to={`/article/${id}`}
      className={`group block bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-300 transition-all duration-300 ${
        featured ? 'lg:col-span-2 lg:row-span-2' : ''
      }`}
    >
      <div className={`relative overflow-hidden ${featured ? 'aspect-[16/7]' : 'aspect-[16/9]'}`}>
        <img
          src={thumbnail}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
          {categoryName}
        </span>
        <div className="absolute bottom-3 right-3 flex items-center gap-3 text-background-50 text-xs">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatCount(views)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-heart-line"></i>
            {formatCount(likes)}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className={`font-heading text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 ${
          featured ? 'text-xl' : 'text-base'
        }`}>
          {title}
        </h3>
        <p className={`text-foreground-500 mt-2 line-clamp-2 ${featured ? 'text-sm' : 'text-xs'}`}>
          {description}
        </p>
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-background-200">
          <img src={author.avatar} alt={author.name} className="w-6 h-6 rounded-full object-cover" />
          <span className="text-xs text-foreground-600">{author.name}</span>
          <span className="text-xs text-foreground-400 ml-auto">{date}</span>
        </div>
      </div>
    </Link>
  )
}